import {
  Conversation,
  ConversationsData,
  Id,
  Member,
  MembersData,
  User,
} from '@nextforce/api'
import { Divider, Icon, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import ConversationCard, {
  ConversationIcon,
} from 'chat/conversations/views/card'
import { filterMembers } from 'chat/members/recipients/util'
import { MemberCard } from 'chat/members/views/card'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View } from 'react-native'
import { FlatList } from 'react-native-gesture-handler'
import Animated, {
  FadeIn,
  FadeOut,
  LinearTransition,
} from 'react-native-reanimated'
import { CheckBox } from 'shared/components/check_box'
import SearchBar from 'shared/components/search_bar'
import { UserAvatar } from 'shared/components/user/user_avatar'
import { ShareResourceRecipient } from '../../types'

interface Props {
  user: User | undefined
  conversationRecipients: ConversationsData | undefined
  memberRecipients: Member[] | MembersData | undefined
  selectedConversationIds: Id[]
  selectedRecipientIds: Id[]
  setSelectedConversationIds: React.Dispatch<React.SetStateAction<Id[]>>
  setSelectedRecipientIds: React.Dispatch<React.SetStateAction<Id[]>>
  acknowledgedUserIds: number[]
}

export function ShareResourceRecipientsView({
  user,
  conversationRecipients,
  memberRecipients,
  selectedConversationIds,
  selectedRecipientIds,
  setSelectedConversationIds,
  setSelectedRecipientIds,
  acknowledgedUserIds,
}: Props) {
  const styles = useStyles()
  const [searchTerm, setSearchTerm] = useState('')

  const filteredCombinedRecipients: ShareResourceRecipient[] = useMemo(() => {
    const conversationItems: ShareResourceRecipient[] =
      conversationRecipients?.map(conversation => ({
        type: 'conversation',
        data: conversation,
      })) || []
    const memberItems: ShareResourceRecipient[] =
      memberRecipients?.map(member => ({
        type: 'member' as const,
        data: member,
      })) || []
    const combinedRecipientsData: ShareResourceRecipient[] = [
      ...conversationItems,
      ...memberItems,
    ]

    const filteredCombinedConversationRecipients =
      combinedRecipientsData.filter(
        item =>
          item.type === 'conversation' &&
          item.data?.name.toLowerCase().includes(searchTerm.toLowerCase())
      )

    const filteredCombinedMemberRecipients: ShareResourceRecipient[] =
      filterMembers(
        combinedRecipientsData
          .filter(item => item.type === 'member')
          .map(item => item.data),
        searchTerm
      ).map(member => ({
        type: 'member',
        data: member,
      }))

    return [
      ...filteredCombinedConversationRecipients,
      ...filteredCombinedMemberRecipients,
    ]
  }, [conversationRecipients, memberRecipients, searchTerm])

  const selectedRecipients: ShareResourceRecipient[] = useMemo(() => {
    const selectedConversations: ShareResourceRecipient[] =
      selectedConversationIds.reduce((acc, id) => {
        const conversation = conversationRecipients?.find(c => c.id === id)
        if (conversation) {
          return [...acc, { type: 'conversation', data: conversation }]
        }
        return acc
      }, [] as ShareResourceRecipient[])
    const selectedMembers: ShareResourceRecipient[] =
      selectedRecipientIds.reduce((acc, id) => {
        const member = memberRecipients?.find(m => m.id === id)
        if (member) {
          return [...acc, { type: 'member', data: member }]
        }
        return acc
      }, [] as ShareResourceRecipient[])
    return [...selectedConversations, ...selectedMembers]
  }, [
    conversationRecipients,
    selectedConversationIds,
    selectedRecipientIds,
    memberRecipients,
  ])

  const isSelectAllDisabled = useMemo(() => {
    return memberRecipients?.every(member =>
      acknowledgedUserIds.includes(Number(member.id))
    )
  }, [memberRecipients, acknowledgedUserIds])

  const selectAllChecked = useMemo(() => {
    const availableRecipients = filteredCombinedRecipients.filter(
      recipient =>
        recipient.type !== 'member' ||
        !acknowledgedUserIds.includes(Number(recipient.data.id))
    )
    return (
      selectedRecipients.length > 0 &&
      selectedRecipients.length === availableRecipients.length
    )
  }, [selectedRecipients, filteredCombinedRecipients, acknowledgedUserIds])

  const onPressConversation = useCallback(
    (conversation: Conversation) => {
      setSelectedConversationIds(prev =>
        prev.includes(conversation.id)
          ? prev.filter(id => id !== conversation.id)
          : [...prev, conversation.id]
      )
    },
    [setSelectedConversationIds]
  )

  const onPressRecipient = useCallback(
    (member: Member) => {
      setSelectedRecipientIds(prev =>
        prev.includes(member.id)
          ? prev.filter(id => id !== member.id)
          : [...prev, member.id]
      )
    },
    [setSelectedRecipientIds]
  )

  const onRemoveRecipient = useCallback(
    (recipient: ShareResourceRecipient) => {
      if (recipient.type === 'conversation') {
        onPressConversation(recipient.data)
      } else {
        onPressRecipient(recipient.data)
      }
    },
    [onPressConversation, onPressRecipient]
  )

  const onToggleSelectAll = useCallback(() => {
    if (selectAllChecked) {
      setSelectedConversationIds([])
      setSelectedRecipientIds([])
    } else {
      const filteredConversations = filteredCombinedRecipients.filter(
        recipient => recipient.type === 'conversation'
      )
      const filteredMembers = filteredCombinedRecipients.filter(
        recipient => recipient.type === 'member'
      )
      setSelectedConversationIds(
        filteredConversations?.map(c => c.data.id) || []
      )
      setSelectedRecipientIds(
        filteredMembers
          ?.filter(m => !acknowledgedUserIds.includes(Number(m.data.id)))
          .map(m => m.data.id) || []
      )
    }
  }, [
    acknowledgedUserIds,
    filteredCombinedRecipients,
    selectAllChecked,
    setSelectedConversationIds,
    setSelectedRecipientIds,
  ])

  const renderItem = useCallback(
    ({ item }) => {
      if (!item || !item.data) {
        console.warn('Invalid item in renderItem:', item)
        return null // Return null for invalid items
      }

      if (item.type === 'conversation') {
        return (
          <ConversationCard
            conversation={item.data}
            user={user}
            onPressConversation={onPressConversation}
            onLongPress={onPressConversation}
            checked={selectedConversationIds.includes(item.data.id)}
            selectable
          />
        )
      } else {
        const isDisabled = acknowledgedUserIds.includes(Number(item.data.id))
        return (
          <MemberCard
            member={item.data}
            onPress={() => onPressRecipient(item.data)}
            checked={selectedRecipientIds.includes(item.data.id) || isDisabled}
            selectable
            showAdminStatus={!isDisabled}
            disabled={isDisabled}
            disabledReason={t('alreadySent')}
          />
        )
      }
    },
    [
      user,
      onPressConversation,
      onPressRecipient,
      selectedConversationIds,
      selectedRecipientIds,
      acknowledgedUserIds,
    ]
  )

  return (
    <View style={styles.container}>
      <View style={styles.searchBarContainer}>
        <SearchBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onPressClear={() => setSearchTerm('')}
        />
      </View>
      <SelectedShareResourceRecipientsRow
        selectedRecipients={selectedRecipients}
        onRemoveRecipient={onRemoveRecipient}
        showSelectAll
        selectAllChecked={selectAllChecked}
        onToggleSelectAll={onToggleSelectAll}
        disabled={isSelectAllDisabled}
      />
      <Animated.View
        layout={LinearTransition}
        style={styles.recipientListContainer}
      >
        <FlatList
          keyboardShouldPersistTaps="handled"
          data={filteredCombinedRecipients}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          ItemSeparatorComponent={() => <Divider />}
          contentContainerStyle={styles.recipientListContentContainer}
        />
      </Animated.View>
    </View>
  )
}

const keyExtractor = (item: ShareResourceRecipient) => {
  return item.type === 'conversation'
    ? `c-${item.data.id}`
    : `m-${item.data.id}`
}

interface SelectedItemsRowProps {
  selectedRecipients: ShareResourceRecipient[]
  onRemoveRecipient: (recipient: ShareResourceRecipient) => void
  showSelectAll?: boolean
  selectAllChecked?: boolean
  onToggleSelectAll?: () => void
  disabled?: boolean
}

export const SelectedShareResourceRecipientsRow = ({
  selectedRecipients,
  onRemoveRecipient,
  showSelectAll,
  selectAllChecked,
  onToggleSelectAll,
  disabled,
}: SelectedItemsRowProps) => {
  const styles = useStyles()
  const flatListRef = useRef<FlatList>(null)
  const prevSelectedRecipientsLength = useRef(selectedRecipients.length)

  useEffect(() => {
    if (selectedRecipients.length > prevSelectedRecipientsLength.current) {
      flatListRef.current?.scrollToEnd({ animated: true })
    }
    prevSelectedRecipientsLength.current = selectedRecipients.length
  }, [selectedRecipients])

  return (
    <View>
      <View style={styles.selectedRecipientsHeaderContainer}>
        <View style={styles.selectedRecipientsHeaderLabelContainer}>
          <Text style={styles.selectedRecipientsHeaderText}>{`${
            selectedRecipients.length
          } ${t('selected')}`}</Text>
        </View>
        {showSelectAll ? (
          <View style={styles.showAllCheckboxContainer}>
            <CheckBox
              checked={selectAllChecked}
              onPress={onToggleSelectAll}
              disabled={disabled}
            />
          </View>
        ) : null}
      </View>

      <Animated.FlatList
        itemLayoutAnimation={LinearTransition}
        keyboardShouldPersistTaps="handled"
        ref={flatListRef}
        horizontal
        data={selectedRecipients}
        keyExtractor={keyExtractor}
        contentContainerStyle={styles.selectedRecipientsContentContainer}
        renderItem={({ item }) => (
          <Animated.View
            entering={FadeIn.duration(200)}
            exiting={FadeOut.duration(200)}
          >
            <SelectedItem
              recipient={item}
              onRemoveRecipient={onRemoveRecipient}
            />
          </Animated.View>
        )}
      />
    </View>
  )
}

const SelectedItem = ({
  recipient,
  onRemoveRecipient,
}: {
  recipient: ShareResourceRecipient
  onRemoveRecipient: (recipient: ShareResourceRecipient) => void
}) => {
  const styles = useStyles()
  return (
    <View style={styles.selectedRecipientItemContainer}>
      {recipient.type === 'conversation' && (
        <ConversationIcon conversation={recipient.data} size={40} />
      )}
      {recipient.type === 'member' && (
        <UserAvatar user={recipient.data} size={40} showAvatarIfNoPhone />
      )}
      <Icon
        name="circle-with-cross"
        type="entypo"
        size={20}
        color={styles.accentColor.color}
        onPress={() => onRemoveRecipient(recipient)}
        containerStyle={styles.selectedRecipientItemCancelIconContainer}
      />
      <Text numberOfLines={1} style={styles.selectedItemName}>
        {recipient.type === 'conversation'
          ? recipient.data.name
          : recipient.data.firstName + ' ' + recipient.data.lastName}
      </Text>
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
  },
  accentColor: {
    color: theme.colors.primary,
  },
  searchBarContainer: {
    marginHorizontal: 5,
    marginTop: 5,
  },
  selectedRecipientsHeaderContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 10,
    paddingVertical: 5,
    backgroundColor: theme.colors.grey7,
    marginHorizontal: 5,
    borderRadius: 10,
  },
  selectedItemName: {
    marginTop: 5,
    width: '90%',
    textAlign: 'center',
    fontSize: 12,
  },
  selectedRecipientsContentContainer: { flexGrow: 1 },
  recipientListContainer: { flex: 1 },
  recipientListContentContainer: {
    flexGrow: 1,
    paddingBottom: 50,
  },
  selectedRecipientsHeaderLabelContainer: { flex: 1, marginLeft: 15 },
  selectedRecipientsHeaderText: { fontSize: 18 },
  showAllCheckboxContainer: { marginRight: 10 },
  selectedRecipientItemContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 70,
    marginVertical: 5,
    paddingVertical: 5,
    marginBottom: 10,
    marginHorizontal: 2,
  },
  selectedRecipientItemCancelIconContainer: {
    position: 'absolute',
    right: 0,
    top: 0,
    borderRadius: 20,
  },
}))
